import { useState } from 'react';
import type { CalendarId } from '@bigint-project/core';
import type { CalendarSummary } from './types.js';

export interface CalendarListPanelProps {
  readonly readOnly?: boolean;
  readonly calendars: readonly CalendarSummary[];
  readonly selectedId: CalendarId | null;
  readonly defaultCalendarId: CalendarId | null;
  readonly onSelect: (id: CalendarId) => void;
  readonly onAdd: (name: string, baseCalendarId: CalendarId | null) => void;
  readonly onDelete: (id: CalendarId) => void;
  readonly onSetDefault: (id: CalendarId) => void;
}

/**
 * 日曆清單：列出專案所有日曆、新增／刪除、設定專案預設日曆，並顯示繼承（base calendar）關係。
 * 純展示元件：props in / callbacks out。零 store、零 Tauri 相依。
 */
export function CalendarListPanel({
  readOnly = false,
  calendars,
  selectedId,
  defaultCalendarId,
  onSelect,
  onAdd,
  onDelete,
  onSetDefault,
}: CalendarListPanelProps): React.JSX.Element {
  const [newName, setNewName] = useState('');
  const [newBaseId, setNewBaseId] = useState<string>('');

  const byId = new Map(calendars.map((c) => [c.id as string, c]));

  const submitAdd = (): void => {
    const name = newName.trim();
    if (name === '') return;
    onAdd(name, newBaseId === '' ? null : (newBaseId as CalendarId));
    setNewName('');
    setNewBaseId('');
  };

  return (
    <div data-testid="calendar-list-panel" className="text-sm">
      <div className="mb-2 font-medium text-fg">專案日曆</div>
      <ul className="mb-3 space-y-1">
        {calendars.map((cal) => {
          const base = cal.baseCalendarId !== null ? byId.get(cal.baseCalendarId as string) : undefined;
          const isSelected = selectedId !== null && cal.id === selectedId;
          const isDefault = defaultCalendarId !== null && cal.id === defaultCalendarId;
          return (
            <li
              key={cal.id as string}
              data-testid={`calendar-list-row-${cal.id as string}`}
              className={`flex items-center justify-between gap-2 rounded px-2 py-1.5 ${
                isSelected ? 'bg-selected' : 'hover:bg-raised'
              }`}
            >
              <button
                type="button"
                className="flex-1 text-left"
                onClick={() => onSelect(cal.id)}
                data-testid={`calendar-list-select-${cal.id as string}`}
              >
                <span className="text-fg">{cal.name}</span>
                {isDefault ? (
                  <span className="ml-1.5 rounded bg-blue-100 px-1 text-[10px] text-blue-700 dark:bg-accent-soft dark:text-accent">
                    專案預設
                  </span>
                ) : null}
                {base !== undefined ? (
                  <div className="text-[11px] text-fg-subtle">繼承自：{base.name}</div>
                ) : cal.baseCalendarId !== null ? (
                  <div className="text-[11px] text-amber-600 dark:text-warn">繼承自：{cal.baseCalendarId as string}（找不到）</div>
                ) : null}
              </button>
              <div className="flex shrink-0 gap-1.5">
                {!isDefault ? (
                  <button
                    type="button"
                    disabled={readOnly}
                    aria-label={`設定 ${cal.name} 為專案預設`}
                    data-testid={`calendar-set-default-${cal.id as string}`}
                    className="text-xs text-blue-600 hover:underline dark:text-focus"
                    onClick={() => onSetDefault(cal.id)}
                  >
                    設為預設
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={readOnly}
                  aria-label={`刪除 ${cal.name}`}
                  data-testid={`calendar-delete-${cal.id as string}`}
                  className="text-xs text-danger hover:opacity-75"
                  onClick={() => onDelete(cal.id)}
                >
                  刪除
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="space-y-1.5 rounded border border-line p-2">
        <div className="text-xs font-medium text-fg-muted">新增日曆</div>
        <input
          disabled={readOnly}
          data-testid="calendar-new-name"
          placeholder="日曆名稱"
          className="w-full rounded border border-line-strong px-1.5 py-1 text-sm text-fg"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <select
          disabled={readOnly}
          data-testid="calendar-new-base"
          className="w-full rounded border border-line-strong px-1.5 py-1 text-sm text-fg"
          value={newBaseId}
          onChange={(e) => setNewBaseId(e.target.value)}
        >
          <option value="">（無 base calendar）</option>
          {calendars.map((cal) => (
            <option key={cal.id as string} value={cal.id as string}>
              繼承自：{cal.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={readOnly}
          data-testid="calendar-new-submit"
          className="w-full rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
          onClick={submitAdd}
        >
          + 新增日曆
        </button>
      </div>
    </div>
  );
}
